import { faKeyboard, faSlash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LogoutButton } from '@modules/Auth';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { UserAvatar } from '@shared/components/ui/UserAvatar';
import { useOperatorPin } from '@shared/hooks/useOperatorPin';
import { useUserData } from '@shared/hooks/useUser';
import { useAuthStore } from '@shared/stores/authStore';
import { isValidOperatorPin, normalizeOperatorPin } from '@shared/utils/operatorPin';
import { useIsMobileAtLoad } from '@shared/hooks/useMobile';
import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';

interface OperatorPinLockScreenProps {
  onUnlock: () => void;
}

export function OperatorPinLockScreen({ onUnlock }: OperatorPinLockScreenProps) {
  const { t } = useTranslation('operatorModule');
  const authUser = useAuthStore((state) => state.user);
  const authTeam = useAuthStore((state) => state.team);
  const { data: userData } = useUserData({ revalidateOnMount: false });
  const { validatePin, isValidatingPin } = useOperatorPin();
  const isMobile = useIsMobileAtLoad();
  const [isKeyboardEnabled, setIsKeyboardEnabled] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const submittingRef = useRef(false);
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);

  const user = userData?.user ?? authUser;
  const team = userData?.team ?? authTeam;
  const displayName = user?.name?.trim() || user?.email || '-';

  useEffect(() => {
    if (isValidatingPin || !error) return;
    const active = document.activeElement;
    if (active === document.body || active === inputRef.current || active?.closest('form') === inputRef.current?.form) {
      inputRef.current?.focus();
    }
  }, [error, isValidatingPin]);

  function toggleKeyboard() {
    if (isValidatingPin || submittingRef.current) return;
    // Aplicar el modo antes del foco, dentro del gesto de usuario, como Picking.
    flushSync(() => setIsKeyboardEnabled((enabled) => !enabled));
    inputRef.current?.blur();
    inputRef.current?.focus();
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isValidatingPin || submittingRef.current) return;
    setError(null);

    if (!isValidOperatorPin(pin)) {
      setError(t('lock.invalidPin'));
      return;
    }

    submittingRef.current = true;
    try {
      const result = await validatePin(pin);
      if (result.valid) {
        setPin('');
        onUnlock();
        return;
      }
      setError(t('lock.invalidPin'));
    } catch {
      setError(t('lock.unexpectedError'));
    } finally {
      submittingRef.current = false;
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100000] flex items-center justify-center bg-background/80 px-4 backdrop-blur-md"
      data-testid="operator-pin-lock-screen"
    >
      <div className="w-full max-w-md rounded-lg border border-card-border bg-card-bg p-6 shadow-lg">
        <h2 className="text-center text-lg font-semibold text-title">{t('lock.title')}</h2>
        <p className="mt-2 text-center text-sm text-complementary-text">{t('lock.description')}</p>

        <div className="mt-5 flex flex-col items-center text-center">
          <UserAvatar
            name={displayName}
            image={user?.profile_photo_path ?? undefined}
            size="md"
            className="mb-3"
            data-testid="operator-lock-user-avatar"
          />
          <p className="text-sm font-medium text-foreground" data-testid="operator-lock-user-name">
            {displayName}
          </p>
          {team?.name ? (
            <p className="mt-0.5 text-xs text-muted-foreground" data-testid="operator-lock-team-name">
              {team.name}
            </p>
          ) : null}
        </div>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div className="relative">
            <Input
              ref={inputRef}
              id="operator-lock-pin-input"
              type="password"
              inputMode={isMobile && !isKeyboardEnabled ? 'none' : 'numeric'}
              autoComplete="off"
              value={pin}
              onChange={(event) => setPin(normalizeOperatorPin(event.target.value))}
              disabled={isValidatingPin}
              className={isMobile ? 'pr-12' : undefined}
              data-testid="operator-lock-pin-input"
              aria-label={t('lock.pinLabel')}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? 'operator-lock-pin-error' : undefined}
              placeholder="••••"
              required
              autoFocus
            />
            {isMobile && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t(isKeyboardEnabled ? 'lock.disableKeyboard' : 'lock.enableKeyboard')}
                title={t(isKeyboardEnabled ? 'lock.disableKeyboard' : 'lock.enableKeyboard')}
                aria-pressed={isKeyboardEnabled}
                disabled={isValidatingPin}
                onClick={toggleKeyboard}
                className="absolute right-1 top-1 h-8 w-8"
              >
                <span className="relative flex h-4 w-4 items-center justify-center" aria-hidden="true">
                  <FontAwesomeIcon icon={faKeyboard} className="h-4 w-4" />
                  {!isKeyboardEnabled && <FontAwesomeIcon icon={faSlash} className="absolute inset-0 h-4 w-4" />}
                </span>
              </Button>
            )}
          </div>

          {error ? (
            <p
              role="alert"
              id="operator-lock-pin-error"
              className="text-sm text-error"
              data-testid="operator-lock-pin-error"
            >
              {error}
            </p>
          ) : null}

          <Button
            type="submit"
            className="w-full"
            loading={isValidatingPin}
            disabled={isValidatingPin}
            data-testid="operator-lock-pin-submit"
          >
            {t('lock.submit')}
          </Button>
        </form>

        <div className="mt-4 border-t border-border pt-4">
          <LogoutButton
            variant="outline"
            size="default"
            className="w-full"
            showIcon
            confirmLogout={false}
            data-testid="operator-lock-logout-button"
          >
            {t('lock.logout')}
          </LogoutButton>
        </div>
      </div>
    </div>
  );
}
